<template>

  <div class="context_list">

    <div class="row">
      <UiInput
        :label="$t('profile_menu_change_password_old_pass')"
        :text="form.old_password.value"
        :error="oldPasswordError"
        :locator="'profile_menu_change_password_old_pass_locator'"
        type="password"
        @change:text="form.old_password.value = $event"
      />
    </div>

    <div class="row">
      <UiInput
        :label="$t('profile_menu_change_password_new_pass')"
        :text="form.new_password.value"
        :error="newPasswordError"
        :locator="'profile_menu_change_password_new_pass_locator'"
        type="password"
        @change:text="form.new_password.value = $event"
        @input="markInteracted('new_password')"
        @blur="markDirty('new_password')"
      />
    </div>

    <div class="row">
      <UiInput
        :label="$t('profile_menu_change_password_new_pass2')"
        :text="form.new_password_repeat.value"
        :error="newPasswordRepeatError"
        :locator="'profile_menu_change_password_new_pass2_locator'"
        type="password"
        @change:text="form.new_password_repeat.value = $event"
        @input="markInteracted('new_password_repeat')"
        @blur="markDirty('new_password_repeat')"
      />
    </div>

    <div v-if="generalError" class="general-error-block">
      {{ generalError }}
    </div>

    <UiButton
        :name="$t('universal_button_save')"
        :type="'primary'"
        :locator="'universal_button_save_locator'"
        @click="handleSavePassword"
        :is-loading="isSaving"
    />

  </div> 

</template>


<script setup lang="ts">
 
 import type { User } from '@/types/user'
 import { useFormValidation } from '~/composables/useFormValidation'
 import { changePasswordFormConfig } from '~/types/validation/formsConfig'

const emits = defineEmits(['close']);

const { $api } = useNuxtApp();
const { t } = useI18n();

interface Props {
  user: User,
  userId: number,
}

const props = defineProps<Props>();

const isSaving = ref<boolean>(false)
const generalError = ref('')
const form = reactive({
  old_password: {
    value: '',
    isInteracted: false,
    isDirty: false
  },
  new_password: {
    value: '',
    isInteracted: false,
    isDirty: false
  },
  new_password_repeat: {
    value: '',
    isInteracted: false,
    isDirty: false
  }
})

const { validateField, markInteracted, markDirty, validateForm } = useFormValidation(form, changePasswordFormConfig)

const validation = {
  old_password: computed(() => validateField('old_password')),
  new_password: computed(() => validateField('new_password')),
  new_password_repeat: computed(() => validateField('new_password_repeat'))
}

const backendErrors = reactive({
  old_password: '',
  new_password: '',
  new_password_repeat: ''
})

const oldPasswordError = computed(() => backendErrors.old_password || validation.old_password.value.errorMessage)
const newPasswordError = computed(() => backendErrors.new_password || validation.new_password.value.errorMessage)
const newPasswordRepeatError = computed(() => backendErrors.new_password_repeat || validation.new_password_repeat.value.errorMessage)

const validate = () => validateForm()

const handleSavePassword = function(){
  backendErrors.old_password = ''
  backendErrors.new_password = ''
  backendErrors.new_password_repeat = ''

  if (!validate()) {
    return false;
  }

  isSaving.value = true;

  $api.patchProfilePassword(props.userId, {
    password_old: form.old_password.value,
    password_new: form.new_password.value,
    password_new2: form.new_password_repeat.value
  })
  .then((res) => {
    isSaving.value = false;
    useToast().success(t('profile_menu_change_password_message_save_success'));
  })
  .catch(function (error) {
    switch (error.message) {
      case 'Current password does not match':
        form.old_password.isDirty = true
        form.old_password.isInteracted = true
        backendErrors.old_password = t('profile_menu_edit_profile_old_pass_wrong')
        break
      case 'Password is not secure':
        form.new_password.isDirty = true
        form.new_password.isInteracted = true
        backendErrors.new_password = t('signup_view_error_password_mask_constraint')
        break
      case 'Passwords do not match':
        form.new_password_repeat.isDirty = true
        form.new_password_repeat.isInteracted = true
        backendErrors.new_password_repeat = t('signup_view_error_password_match')
        break
      default:
        generalError.value = t('universal_generic_error')
    }
    isSaving.value = false;
  });
}
 

</script>

<style scoped>
  .ctx{        
    min-width: 59px;
    display: flex;
    justify-content: center;
  }
  .ctx > i{    
    padding: 14px 20px 14px 25px;
    cursor: pointer;
  }


  /*  */

  .context_list{ 
    max-width: 400px;
    /* margin: 0 auto; */
    display: flex;
    gap: 0.5rem;
    flex-direction: column;
    align-items: flex-start;
  }

  .context_list .row,
  .context_list .field_input,
  .context_list input{
    width: 100%;

  }
 
</style>
